import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, DateInput, Modal } from '@safetyhub/ui';
import {
  syncLogsApi,
  DataSyncSource,
  DataSyncStatus,
  ScheduleInfo,
  SyncLogEntry,
  SyncLogSummary,
  SYNC_SOURCE_LABELS,
  SYNC_SUBTYPE_LABELS,
  SYNC_SOURCE_PATHS,
} from '../lib/sync-logs';
import { localDayEndIso, localDayStartIso } from '../lib/localDayBounds';

const ALL_SOURCES: DataSyncSource[] = [
  'DESIGNATED_INSTITUTIONS',
  'KOSHA_GUIDE',
  'REGULATIONS',
  'ACCIDENT_CASES',
  'INDUSTRIES',
];

const PAGE_SIZE_OPTIONS = [50, 100, 200];

const SYNC_STATUS_LABELS: Record<DataSyncStatus, string> = {
  SUCCESS: '정상',
  FAILED: '실패',
  PARTIAL: '부분 성공',
};

const SYNC_STATUS_CLASSES: Record<DataSyncStatus, string> = {
  SUCCESS: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
  PARTIAL: 'bg-yellow-100 text-yellow-700',
};

// source별 subType 목록 정의
const SUBTYPE_OPTIONS: Partial<Record<DataSyncSource, string[]>> = {
  DESIGNATED_INSTITUTIONS: ['CONSTRUCTION_GUIDANCE', 'SAFETY_MANAGEMENT', 'HEALTH_MANAGEMENT'],
};

function formatLocalDateTime(isoString: string | null): string {
  if (!isoString) return '-';
  const d = new Date(isoString);
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const totalSec = ms / 1000;
  if (totalSec < 60) return `${totalSec.toFixed(1)}초`;
  const min = Math.floor(totalSec / 60);
  const sec = Math.round(totalSec % 60);
  return sec > 0 ? `${min}분 ${sec}초` : `${min}분`;
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
      />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function formatNextRunAt(isoString: string): string {
  const d = new Date(isoString);
  const month = d.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' });
  const time = d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${month} ${time}`;
}

function ScheduleBadge({ schedule }: { schedule: ScheduleInfo | null }) {
  return (
    <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
      <ClockIcon className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" />
      {schedule ? (
        <span>
          {schedule.description} · 다음: {formatNextRunAt(schedule.nextRunAt)}
        </span>
      ) : (
        <span>수동 동기화</span>
      )}
    </div>
  );
}

function SyncStatusBadge({ lastSync }: { lastSync: SyncLogSummary | null }) {
  if (!lastSync) {
    return (
      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
        미실행
      </span>
    );
  }
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${SYNC_STATUS_CLASSES[lastSync.status]}`}
    >
      {SYNC_STATUS_LABELS[lastSync.status]}
    </span>
  );
}

function ErrorExpandable({ message }: { message: string }) {
  const [expanded, setExpanded] = useState(false);
  const truncated = message.length > 80 ? message.slice(0, 80) + '...' : message;
  return (
    <div className="mt-1 text-xs text-red-600">
      <span>{expanded ? message : truncated}</span>
      {message.length > 80 && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="ml-1 underline hover:text-red-800"
        >
          {expanded ? '접기' : '더 보기'}
        </button>
      )}
    </div>
  );
}

// 상단 상태 카드
function StatsCards() {
  const navigate = useNavigate();
  const { data: stats, isLoading } = useQuery({
    queryKey: ['admin', 'sync-logs', 'stats'],
    queryFn: () => syncLogsApi.getStats(),
    staleTime: 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} radius="lg" className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
            <div className="h-5 bg-gray-200 rounded w-1/2 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-full" />
          </Card>
        ))}
      </div>
    );
  }

  // stats에서 카드 항목 추출
  const cards: Array<{
    source: DataSyncSource;
    subType: string;
    label: string;
    lastSync: SyncLogSummary | null;
    recentFailureCount: number;
    schedule: ScheduleInfo | null;
  }> = [];

  ALL_SOURCES.forEach((source) => {
    const sourceData = stats?.[source];
    const schedule = sourceData?.schedule ?? null;
    if (!sourceData) {
      cards.push({ source, subType: '_default', label: SYNC_SOURCE_LABELS[source], lastSync: null, recentFailureCount: 0, schedule: null });
      return;
    }
    const subTypeEntries = Object.entries(sourceData.subTypes);
    if (subTypeEntries.length === 0) {
      cards.push({ source, subType: '_default', label: SYNC_SOURCE_LABELS[source], lastSync: null, recentFailureCount: 0, schedule });
      return;
    }
    subTypeEntries.forEach(([subType, data]) => {
      const subTypeLabel =
        subType === '_default'
          ? SYNC_SOURCE_LABELS[source]
          : `${SYNC_SOURCE_LABELS[source]} - ${SYNC_SUBTYPE_LABELS[subType] ?? subType}`;
      cards.push({ source, subType, label: subTypeLabel, lastSync: data.lastSync, recentFailureCount: data.recentFailureCount ?? 0, schedule });
    });
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
      {cards.map(({ source, subType, label, lastSync, recentFailureCount, schedule }) => {
        const domainPath = SYNC_SOURCE_PATHS[source];
        const hasData = lastSync !== null;
        return (
          <Card
            key={`${source}-${subType}`}
            radius="lg"
            className={!hasData ? 'opacity-60' : ''}
          >
            <div className="flex items-start justify-between mb-2 gap-2">
              <p className="text-xs font-medium text-gray-700 leading-snug flex-1">{label}</p>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {recentFailureCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-semibold">
                    {recentFailureCount > 99 ? '99+' : recentFailureCount}
                  </span>
                )}
                {domainPath && (
                  <button
                    onClick={() => navigate(domainPath)}
                    className="text-gray-400 hover:text-gray-600"
                    title="도메인 페이지로 이동"
                  >
                    <ExternalLinkIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            <div className="mb-2">
              <SyncStatusBadge lastSync={lastSync} />
            </div>

            {hasData ? (
              <div className="text-xs text-gray-500 space-y-0.5">
                <p>
                  마지막 시도:{' '}
                  <span className={lastSync.status === 'FAILED' ? 'text-red-500' : ''}>
                    {formatLocalDateTime(lastSync.startedAt)}
                  </span>
                </p>
                <p className="text-gray-400">{formatDuration(lastSync.durationMs)} 소요</p>
                {lastSync.status === 'SUCCESS' || lastSync.status === 'PARTIAL' ? (
                  <p>{lastSync.itemCount.toLocaleString()}건 처리</p>
                ) : null}
                {lastSync.errorMessage && (
                  <ErrorExpandable message={lastSync.errorMessage} />
                )}
              </div>
            ) : (
              <p className="text-xs text-gray-400">동기화 이력 없음</p>
            )}

            <ScheduleBadge schedule={schedule} />
          </Card>
        );
      })}
    </div>
  );
}

// 상세 모달
function LogDetailModal({
  entry,
  onClose,
}: {
  entry: SyncLogEntry;
  onClose: () => void;
}) {
  const sourceLabel = SYNC_SOURCE_LABELS[entry.source] ?? entry.source;
  const subTypeLabel =
    entry.subType && entry.subType !== '_default'
      ? SYNC_SUBTYPE_LABELS[entry.subType] ?? entry.subType
      : null;

  // 부모가 열 때만 마운트하므로 open은 늘 true다(읽기 전용 상세 — 잠글 처리 단계가 없다).
  return (
    <Modal open onClose={onClose} title="동기화 상세" size="lg">
      <dl className="space-y-3 text-sm">
        <div className="flex justify-between">
          <dt className="text-gray-500">소스</dt>
          <dd className="text-gray-900 font-medium">
            {sourceLabel}
            {subTypeLabel && <span className="text-gray-500"> / {subTypeLabel}</span>}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500">상태</dt>
          <dd>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${SYNC_STATUS_CLASSES[entry.status]}`}>
              {SYNC_STATUS_LABELS[entry.status]}
            </span>
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500">시작</dt>
          <dd className="text-gray-900">{formatLocalDateTime(entry.startedAt)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500">완료</dt>
          <dd className="text-gray-900">{formatLocalDateTime(entry.finishedAt)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500">소요 시간</dt>
          <dd className="text-gray-900">{formatDuration(entry.durationMs)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-gray-500">처리 건수</dt>
          <dd className="text-gray-900">{entry.itemCount.toLocaleString()}건</dd>
        </div>
        {entry.errorMessage && (
          <div>
            <dt className="text-gray-500 mb-1">오류 메시지</dt>
            <dd className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 break-all">
              {entry.errorMessage}
            </dd>
          </div>
        )}
        {entry.errorCause && (
          <div>
            <dt className="text-gray-500 mb-1">원인 (stack)</dt>
            <dd className="p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700 break-all whitespace-pre-wrap max-h-40 overflow-auto">
              {entry.errorCause}
            </dd>
          </div>
        )}
      </dl>
    </Modal>
  );
}

export default function SyncHistory() {
  const [sourceFilter, setSourceFilter] = useState<DataSyncSource | ''>('');
  const [subTypeFilter, setSubTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<DataSyncStatus | ''>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [selectedEntry, setSelectedEntry] = useState<SyncLogEntry | null>(null);

  const subTypeOptions = sourceFilter ? (SUBTYPE_OPTIONS[sourceFilter] ?? []) : [];

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      'admin',
      'sync-logs',
      {
        source: sourceFilter || undefined,
        subType: subTypeFilter || undefined,
        status: statusFilter || undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        page,
        limit,
      },
    ],
    queryFn: () =>
      syncLogsApi.list({
        source: (sourceFilter as DataSyncSource) || undefined,
        subType: subTypeFilter || undefined,
        status: (statusFilter as DataSyncStatus) || undefined,
        // 'YYYY-MM-DD'를 그대로 보내면 서버가 UTC 자정으로 읽는다 — 로컬 하루 경계로(lib/localDayBounds).
        from: localDayStartIso(fromDate),
        to: localDayEndIso(toDate),
        page,
        limit,
      }),
    staleTime: 30 * 1000,
  });

  const entries = data?.data ?? [];
  const meta = data?.meta ?? { page: 1, limit, total: 0 };
  const totalPages = Math.ceil(meta.total / meta.limit);

  const handleSourceChange = (value: DataSyncSource | '') => {
    setSourceFilter(value);
    setSubTypeFilter('');
    setPage(1);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">데이터 동기화 이력</h1>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-control hover:bg-gray-50 transition-colors text-gray-600"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          새로고침
        </button>
      </div>

      {/* 상단 상태 카드 */}
      <StatsCards />

      {/* 필터 영역 */}
      <Card radius="lg" className="mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          {/* source 필터 */}
          <select
            value={sourceFilter}
            onChange={(e) => handleSourceChange(e.target.value as DataSyncSource | '')}
            className="border border-gray-300 rounded-control px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="">전체 소스</option>
            {ALL_SOURCES.map((s) => (
              <option key={s} value={s}>
                {SYNC_SOURCE_LABELS[s]}
              </option>
            ))}
          </select>

          {/* subType 필터 */}
          <select
            value={subTypeFilter}
            onChange={(e) => {
              setSubTypeFilter(e.target.value);
              setPage(1);
            }}
            disabled={!sourceFilter || subTypeOptions.length === 0}
            className="border border-gray-300 rounded-control px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <option value="">전체 유형</option>
            {subTypeOptions.map((st) => (
              <option key={st} value={st}>
                {SYNC_SUBTYPE_LABELS[st] ?? st}
              </option>
            ))}
          </select>

          {/* 상태 필터 */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as DataSyncStatus | '');
              setPage(1);
            }}
            className="border border-gray-300 rounded-control px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="">전체 상태</option>
            <option value="SUCCESS">정상</option>
            <option value="FAILED">실패</option>
            <option value="PARTIAL">부분 성공</option>
          </select>

          {/* 기간 필터 */}
          <div className="flex items-center gap-2">
            {/* 보이는 라벨이 없는 줄이라 placeholder 가 그 역할을 겸한다 — 접근명은 `label` 이 소유한다. */}
            <DateInput
              label="조회 시작일"
              placeholder="시작일"
              value={fromDate}
              onChange={(next) => {
                setFromDate(next);
                setPage(1);
              }}
            />
            <span className="text-gray-400 text-sm">~</span>
            <DateInput
              label="조회 종료일"
              placeholder="종료일"
              value={toDate}
              onChange={(next) => {
                setToDate(next);
                setPage(1);
              }}
            />
          </div>

          {/* 페이지 크기 */}
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="border border-gray-300 rounded-control px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            {PAGE_SIZE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}개씩
              </option>
            ))}
          </select>

          <span className="text-sm text-gray-500">총 {meta.total.toLocaleString()}건</span>
        </div>
      </Card>

      {/* 이력 테이블 */}
      <Card padding="none" radius="lg" className="overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <SpinnerIcon className="w-6 h-6 animate-spin mr-2" />
            불러오는 중...
          </div>
        ) : entries.length === 0 ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
            이력이 없습니다.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 whitespace-nowrap">소스</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 whitespace-nowrap">유형</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 whitespace-nowrap">상태</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 whitespace-nowrap">시작 시각</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500 whitespace-nowrap">소요</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500 whitespace-nowrap">건수</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">오류</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map((entry) => {
                    const sourceLabel = SYNC_SOURCE_LABELS[entry.source] ?? entry.source;
                    const subTypeLabel =
                      entry.subType && entry.subType !== '_default'
                        ? SYNC_SUBTYPE_LABELS[entry.subType] ?? entry.subType
                        : '-';
                    return (
                      <tr
                        key={entry.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelectedEntry(entry)}
                      >
                        <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap">{sourceLabel}</td>
                        <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{subTypeLabel}</td>
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${SYNC_STATUS_CLASSES[entry.status]}`}
                          >
                            {SYNC_STATUS_LABELS[entry.status]}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap text-xs">
                          {formatLocalDateTime(entry.startedAt)}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600 text-right whitespace-nowrap text-xs">
                          {formatDuration(entry.durationMs)}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600 text-right whitespace-nowrap">
                          {entry.itemCount.toLocaleString()}
                        </td>
                        <td className="px-4 py-2.5 text-red-600 text-xs max-w-[200px] truncate">
                          {entry.errorMessage ?? '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  이전
                </button>
                <span className="text-sm text-gray-600">
                  {page} / {totalPages} 페이지
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  다음
                </button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* 상세 모달 */}
      {selectedEntry && (
        <LogDetailModal entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
      )}
    </div>
  );
}
